/** @prettier */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { usePrevious } from '@xanadu/ui-shared/hooks';
import { track } from '@xanadu/ui-shared/interfaces/woopra';

import IconTrash from '../../../../images/icons/misc/trash-black.svg';

import style from './Selection.module.scss';

const Selection = ({
  id,
  cid,
  runnerName,
  side,
  marketId,
  eventId,
  marketName,
  eventName,
  sportUrlName,
  gradingResult,
  adjustments = [],
  index,
  readOnly = false,
  errors = [],
  requestError = '',
  removeSelectionFromMultipleByCids = () => {},
  removeOffersByCids = () => {},
  onUpdateSelection = () => {},
  subscribeToEvent = () => {},
  unsubscribeToEvent = () => {},
  subscribeToMarket = () => {},
  unsubscribeToMarket = () => {},
  updateRightSidebar,
  widthSize,
}) => {
  const previousErrors = usePrevious(errors);
  const previousRequestError = usePrevious(requestError);

  useEffect(() => {
    const errorMessage = errors.length > 0 ? errors[0].message : '';
    onUpdateSelection('add', id, errors.length > 0, errorMessage);

    subscribeToEvent(eventId);
    subscribeToMarket(marketId);

    return () => {
      onUpdateSelection('remove', id);
      unsubscribeToEvent(eventId);
      unsubscribeToMarket(marketId);
    };
  }, []);

  useEffect(() => {
    if (previousErrors && errors.length !== previousErrors.length) {
      const errorMessage = errors.length > 0 ? errors[0].message : '';
      onUpdateSelection('update', id, errors.length > 0, errorMessage);
    }
  }, [errors, previousErrors]);

  useEffect(() => {
    if (requestError !== previousRequestError) {
      onUpdateSelection('update', id, requestError !== '', requestError);
    }
  }, [requestError, previousRequestError]);

  const isEnhancedSpecialsSport = ['enhanced-specials', 'podcast-specials'].includes(sportUrlName);

  const mainClass = classNames({
    [style.main]: true,
    [style['main--enhancedSpecialsSportWarning']]: isEnhancedSpecialsSport,
    [style['main--void']]: gradingResult === 'VOIDED',
    [style['main--error']]: errors.length || requestError,
  });

  const legNumberErrorClass = classNames({
    [style.legNumber]: true,
    [style['legNumber--multipleLegs']]: errors[0] && errors[0].key === 'multiple-legs',
  });

  const rightContainerClass = classNames({
    [style['rightContainer--readOnly']]: readOnly,
  });

  const deductionFactorApplied = adjustments.find((a) => a.reason === 'DF');
  let result = gradingResult;
  let errorMessage = null;

  if (gradingResult === 'VOIDED') {
    result = 'Void';
  } else if (gradingResult === 'ACTIVE') {
    result = '';
  }

  if (!isEnhancedSpecialsSport) {
    errorMessage = (
      <span className={style.errorMessage}>
        {(errors.length && errors[0].message) || requestError}
      </span>
    );
  }

  return (
    <div className={mainClass}>
      {isEnhancedSpecialsSport && (
        <div className={style.enhancedSpecialsSportWarning}>
          <span>Enhanced Specials should be placed as exchange offers</span>
          <a
            role="button"
            onClick={() => {
              updateRightSidebar({
                visible: true,
                content: 'betslip',
              });
            }}
          >
            Show Me
          </a>
        </div>
      )}
      <div className={style.leftContainer}>
        <div className={style.containerNumber}>
          <div className={`${style.legNumber} ${legNumberErrorClass}`}>{index}</div>
        </div>
        <div className={style.containerInfo}>
          {result === 'WON' ? (
            <div>
              <div className={`${style.runner} ${style[`runner--${side}`]}`} title={runnerName}>
                {runnerName}
              </div>
              <div className={style.event} title={eventName}>
                {eventName}
              </div>
              <div className={style.market}>{marketName}</div>
            </div>
          ) : (
            <Link
              className={style.link}
              to={`/events/${eventId}`}
              onClick={() => {
                if (widthSize < 1200) {
                  updateRightSidebar({
                    visible: false,
                  });
                }
              }}
            >
              <div className={`${style.runner} ${style[`runner--${side}`]}`} title={runnerName}>
                {runnerName}
              </div>
              <div className={style.event} title={eventName}>
                {eventName}
              </div>
              <div className={style.market}>{marketName}</div>
            </Link>
          )}
        </div>
      </div>
      <div className={`${style.rightContainer} ${rightContainerClass}`}>
        {gradingResult === 'VOIDED' ? (
          <div className={style.containerOdds}>
            <div className={style.voidStatus}>{result}</div>
          </div>
        ) : (
          <div className={style.containerOdds}>
            <div className={`${style.odds} ${style[`odds--${side}`]}`}>
              {deductionFactorApplied && <span className={style.oddsDeductionFactor}>(DF)</span>}
            </div>
            <div className={style.status}>{result}</div>
          </div>
        )}
        {!readOnly && (
          <div className={style.containerAction}>
            <div
              role="button"
              className={style.action}
              onClick={() => {
                removeSelectionFromMultipleByCids([cid]);
                removeOffersByCids([cid]);

                track('Accas', 'Leg deletion', {
                  description: `Reason: ${requestError || errors.map((e) => e.message).join(', ')}`,
                });
              }}
            >
              <IconTrash className={style.icon} />
            </div>
          </div>
        )}
      </div>
      {errorMessage}
    </div>
  );
};

Selection.propTypes = {
  id: PropTypes.string.isRequired,
  cid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  runnerName: PropTypes.string.isRequired,
  side: PropTypes.string.isRequired,
  marketId: PropTypes.string.isRequired,
  eventId: PropTypes.string.isRequired,
  marketName: PropTypes.string.isRequired,
  eventName: PropTypes.string.isRequired,
  sportUrlName: PropTypes.string.isRequired,
  gradingResult: PropTypes.string,
  adjustments: PropTypes.array,
  index: PropTypes.number.isRequired,
  readOnly: PropTypes.bool,
  errors: PropTypes.array,
  requestError: PropTypes.string,
  removeSelectionFromMultipleByCids: PropTypes.func,
  removeOffersByCids: PropTypes.func,
  onUpdateSelection: PropTypes.func,
  subscribeToEvent: PropTypes.func,
  unsubscribeToEvent: PropTypes.func,
  subscribeToMarket: PropTypes.func,
  unsubscribeToMarket: PropTypes.func,
  updateRightSidebar: PropTypes.func.isRequired,
  widthSize: PropTypes.number.isRequired,
};

export default Selection;
